import { LiquidProduct } from '@/features/product/types'
import { useMemo } from 'react'
import { LinkedItemConfiguration, Option, ProductValues } from '../types'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

const oldUseDefaultValues = (
  options: Option[],
  links: LinkedItemConfiguration[],
  variants: LiquidProduct['variants'],
  selectedVariant: LiquidProduct['variants'][number] | null,
) => {
  const defaultValues = useMemo<ProductValues>(() => {
    const defaultVariant: LiquidProduct['variants'][number] | undefined =
      selectedVariant ??
      variants.find((variant) => variant.in_stock) ??
      variants[0]

    return {
      options: defaultVariant?.options ?? [],
      variant: defaultVariant,
      links: links.map((link) => {
        let defaultLinkItem = link.items[0]
        let defaultLinkVariant = link.items[0]?.variants[0]

        for (const item of link.items) {
          const variant = item.variants.find(({ in_stock }) => in_stock)
          if (variant) {
            defaultLinkItem = item
            defaultLinkVariant = variant
            break
          }
        }

        return {
          id: defaultLinkItem.id.toString(),
          name: defaultLinkItem.title,
          options: defaultLinkVariant?.options ?? [],
          variant: defaultLinkVariant,
        }
      }),
    }
  }, [options, links])

  return defaultValues
}

const newUseDefaultValues = (
  options: Option[],
  links: LinkedItemConfiguration[],
  variants: LiquidProduct['variants'],
  selectedVariant: LiquidProduct['variants'][number] | null,
  hasOnlyDefaultVariant: boolean = false,
) => {
  const defaultValues = useMemo<ProductValues>(() => {
    const defaultVariant: LiquidProduct['variants'][number] | undefined =
      selectedVariant ?? (hasOnlyDefaultVariant ? variants[0] : undefined)

    return {
      options: defaultVariant?.options ?? options.map(() => undefined),
      variant: defaultVariant,
      links: links.map((link) => {
        let defaultLinkItem = link.items[0] ?? undefined
        let defaultLinkVariant = link.items[0]?.variants[0] ?? undefined

        for (const item of link.items) {
          const variant = item.variants.find(({ in_stock }) => in_stock)
          if (variant) {
            defaultLinkItem = item
            defaultLinkVariant = variant
            break
          }
        }

        return {
          id: defaultLinkItem.id.toString(),
          name: defaultLinkItem.title,
          options: [],
          variant: undefined,
        }
      }),
    }
  }, [options, links, hasOnlyDefaultVariant])

  return defaultValues
}

export const useDefaultValues = (
  options: Option[],
  links: LinkedItemConfiguration[],
  variants: LiquidProduct['variants'],
  selectedVariant: LiquidProduct['variants'][number] | null,
  hasOnlyDefaultVariant: boolean = false,
) => {
  const features = useFeatureFlags()

  return features.getFlag('product_choice_chips')
    ? newUseDefaultValues(
        options,
        links,
        variants,
        selectedVariant,
        hasOnlyDefaultVariant,
      )
    : oldUseDefaultValues(options, links, variants, selectedVariant)
}
